import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Container,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  Fade,
  Slide,
  Avatar,
  Stack,
  Chip,
  Grid,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Restaurant,
  AdminPanelSettings,
  Security,
  Login as LoginIcon,
  ShieldOutlined,
  LockOutlined,
  PersonOutline,
  RestaurantMenuOutlined,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useCustomTheme } from '../contexts/ThemeContext';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login, isAuthenticated, loading } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isDarkMode, theme } = useCustomTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const from = location.state?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, from]);

  // If already authenticated, redirect immediately
  if (isAuthenticated && !loading) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData.username.trim() || !formData.password) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    try {
      console.log('Attempting login with:', formData.username);
      const result = await login(formData);
      
      if (result.success) {
        console.log('Login successful, preparing to navigate');
        showSuccess('Login successful! Welcome to the admin panel.');
        
        // Longer delay to ensure authentication state is fully updated before navigation
        setTimeout(() => {
          console.log('Navigating to:', from);
          navigate(from, { replace: true });
        }, 800);
      } else {
        console.error('Login failed:', result.error);
        setError(result.error);
        showError(result.error);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isDarkMode ? `
          radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(244, 63, 94, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(245, 158, 11, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #0f172a 0%, #1e293b 100%)
        ` : `
          radial-gradient(circle at 20% 50%, rgba(25, 118, 210, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(220, 0, 78, 0.15) 0%, transparent 50%),
          radial-gradient(circle at 40% 80%, rgba(255, 152, 0, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)
        `,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: `linear-gradient(45deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: '80px',
          height: '80px',
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          background: `linear-gradient(-45deg, ${theme.palette.warning.main}20, ${theme.palette.info.main}20)`,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Grid container spacing={0} alignItems="center" sx={{ minHeight: '80vh' }}>
            {/* Left Side - Branding */}
            {!isTablet && (
              <Grid item xs={12} md={6}>
                <Slide direction="right" in timeout={1000}>
                  <Box sx={{ pr: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 4,
                        position: 'relative',
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 120,
                          height: 120,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          boxShadow: '0 20px 40px rgba(25, 118, 210, 0.3)',
                          mr: 2,
                        }}
                      >
                        <RestaurantMenuOutlined sx={{ fontSize: 60 }} />
                      </Avatar>
                      <Avatar
                        sx={{
                          width: 100,
                          height: 100,
                          background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.warning.main})`,
                          boxShadow: '0 15px 30px rgba(220, 0, 78, 0.3)',
                          position: 'absolute',
                          right: '30%',
                          top: '20%',
                        }}
                      >
                        <AdminPanelSettings sx={{ fontSize: 50 }} />
                      </Avatar>
                    </Box>

                    <Typography
                      variant="h2"
                      fontWeight="800"
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        color: 'transparent',
                        mb: 2,
                      }}
                    >
                      Restaurant Pro
                    </Typography>
                    
                    <Typography variant="h5" color="text.secondary" fontWeight="300" sx={{ mb: 4 }}>
                      Advanced Management System
                    </Typography>

                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                      <Chip
                        icon={<Restaurant />}
                        label="Multi-Outlet"
                        variant="outlined"
                        color="primary"
                        sx={{ borderRadius: 3 }}
                      />
                      <Chip
                        icon={<AdminPanelSettings />}
                        label="Admin Panel"
                        variant="outlined"
                        color="secondary"
                        sx={{ borderRadius: 3 }}
                      />
                      <Chip
                        icon={<ShieldOutlined />}
                        label="Secure"
                        variant="outlined"
                        color="success"
                        sx={{ borderRadius: 3 }}
                      />
                    </Stack>

                    <Box sx={{ mt: 6, opacity: 0.7 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Trusted by 1000+ restaurants worldwide
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Box
                            key={star}
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Slide>
              </Grid>
            )}

            {/* Right Side - Login Form */}
            <Grid item xs={12} md={isTablet ? 12 : 6}>
              <Slide direction="left" in timeout={1200}>
                <Paper
                  elevation={0}
                  sx={{
                    p: isMobile ? 3 : isTablet ? 4 : 5,
                    borderRadius: 4,
                    background: isDarkMode 
                      ? 'rgba(30, 41, 59, 0.95)' 
                      : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: isDarkMode 
                      ? '1px solid rgba(51, 65, 85, 0.3)' 
                      : '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: isDarkMode 
                      ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
                      : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Decorative Elements */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -50,
                      right: -50,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
                    }}
                  />

                  {/* Header */}
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        boxShadow: '0 10px 30px rgba(25, 118, 210, 0.3)',
                      }}
                    >
                      <LockOutlined sx={{ fontSize: 40 }} />
                    </Avatar>
                    
                    <Typography variant="h4" fontWeight="700" gutterBottom>
                      Welcome Back
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Sign in to your admin dashboard
                    </Typography>
                  </Box>

                  {/* Error Alert */}
                  {error && (
                    <Fade in>
                      <Alert 
                        severity="error" 
                        sx={{ 
                          mb: 3, 
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'error.light',
                        }}
                      >
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  {/* Login Form */}
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <TextField
                      fullWidth
                      name="username"
                      label="Username"
                      value={formData.username}
                      onChange={handleChange}
                      margin="normal"
                      required
                      autoComplete="username"
                      autoFocus
                      disabled={isLoading}
                      sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: 'rgba(248, 250, 252, 0.8)',
                          '&:hover': {
                            backgroundColor: 'rgba(248, 250, 252, 1)',
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutline color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      margin="normal"
                      required
                      autoComplete="current-password"
                      disabled={isLoading}
                      sx={{
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: 'rgba(248, 250, 252, 0.8)',
                          '&:hover': {
                            backgroundColor: 'rgba(248, 250, 252, 1)',
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              edge="end"
                              disabled={isLoading}
                              sx={{ color: 'text.secondary' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={isLoading}
                      startIcon={isLoading ? null : <LoginIcon />}
                      sx={{
                        py: 2,
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        boxShadow: '0 10px 40px rgba(25, 118, 210, 0.4)',
                        textTransform: 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                          boxShadow: '0 15px 50px rgba(25, 118, 210, 0.5)',
                          transform: 'translateY(-2px)',
                        },
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                          transition: 'left 0.5s',
                        },
                        '&:hover:before': {
                          left: '100%',
                        },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Sign In to Dashboard'
                      )}
                    </Button>
                  </Box>

                  {/* <Divider sx={{ my: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Demo Credentials
                    </Typography>
                  </Divider> */}

                  {/* Demo Credentials */}
                  {/* <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'rgba(25, 118, 210, 0.05)',
                      border: '1px solid rgba(25, 118, 210, 0.1)',
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="primary.main" fontWeight="600" gutterBottom>
                      Quick Login
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                      <Chip
                        label="admin"
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => setFormData(prev => ({ ...prev, username: 'admin' }))}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Chip
                        label="adminpassword"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        onClick={() => setFormData(prev => ({ ...prev, password: 'adminpassword' }))}
                        sx={{ cursor: 'pointer' }}
                      />
                    </Stack>
                  </Paper> */}
                </Paper>
              </Slide>
            </Grid>
          </Grid>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
